// Keeps the buy buttons locked until a spring force is chosen, narrows the list to what the
// selected length takes, and carries the force into the cart.
//
// The product form is rendered with novalidate, so the select's own `required` attribute cannot
// stop a submit: the buttons are disabled instead. Buy it now does not use Shopify's dynamic
// checkout button, because a line item property is mandatory here and the accelerated buttons
// are not documented to carry one; the line goes through the cart API with the property
// attached and the customer is then sent to the checkout.
class ForceSelect extends HTMLElement {
  connectedCallback() {
    // Set up on connection rather than in the constructor, because this element reaches
    // outside itself for the form and the hint. The complementary products block fetches
    // this whole section and parses it into a detached div, which still upgrades the custom
    // elements inside it: a throwaway copy set up in a constructor would reach out of its
    // div, find the live page's buttons by id, and lock them on behalf of a select nobody
    // can see. A detached copy is never connected, so it never runs any of this.
    if (!this.ready) this.setup();
    if (!this.ready) return;

    // The theme only re-renders price, SKU and inventory on a variant change, so the list of
    // forces has to be refiltered here.
    if (typeof subscribe !== 'function') return;
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      // Availability is read off the freshly fetched section, never off the live button:
      // this element writes `disabled` on that button itself, so by the time this runs the
      // button is only repeating what we last told it. The new markup is the theme's own
      // verdict, and it already accounts for stock, quantity rules and a missing variant.
      const rendered = event.data.html?.getElementById(`ProductSubmitButton-${event.data.sectionId}`);
      if (rendered) this.available = !rendered.hasAttribute('disabled');
      this.applyCap(event.data.variant && event.data.variant.id);
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    this.unsubscribe = null;
  }

  setup() {
    this.select = this.querySelector('select');
    // Beside the variant picker the field sits outside the form and reaches it by id, the
    // way the picker's own selects do. Inside the form -- where the featured product section
    // still renders it -- the ancestor is still the answer.
    this.form = this.closest('form') || document.getElementById(this.dataset.formId || '');
    if (!this.select || !this.form) return;

    const caps = this.querySelector('[data-force-caps]');
    this.caps = caps ? JSON.parse(caps.textContent) : {};
    this.options = Array.from(this.select.options).filter((option) => option.dataset.newtons);

    this.buttons = Array.from(this.form.querySelectorAll('button[type="submit"]'));

    // Whether the variant can be bought at all. Tracked rather than assumed, because this
    // element writes `disabled` on the same buttons the theme does: choosing a force used to
    // clear the flag the theme had set on a sold out variant and hand back a live "Sold out"
    // button. The submit button as the theme last rendered it is the most complete answer --
    // it already accounts for stock, quantity rules and a variant that does not exist.
    this.submit = this.buttons.find((button) => button.classList.contains('product-form__submit'));
    this.available = !this.submit || !this.submit.disabled;

    // The sentence explaining the lock lives beside the buttons, so it is found the same way
    // the select points at it.
    this.hint = document.getElementById(this.select.getAttribute('aria-describedby') || '');

    this.buyNow = this.form.querySelector('[data-force-buy-now]');
    if (this.buyNow) {
      this.buttons.push(this.buyNow);
      this.buyNow.addEventListener('click', this.checkout.bind(this));
    }

    this.select.addEventListener('change', this.update.bind(this));

    // Dawn sets input[name="id"] and fires a bubbling change on every variant switch. Listening
    // for it here keeps the cap in step even where the pubsub globals are not reachable.
    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'id') this.applyCap(event.target.value);
    });

    this.ready = true;
    this.update();
  }

  applyCap(variantId) {
    const cap = this.caps[variantId];
    this.options.forEach((option) => {
      const over = cap !== undefined && Number(option.dataset.newtons) > cap;
      option.hidden = over;
      option.disabled = over;
      // A force that the new length cannot take stops being a valid choice.
      if (over && option.selected) this.select.value = '';
    });
    this.update();
  }

  update() {
    const chosen = this.select.value !== '';
    const waiting = !chosen && this.available;
    this.buttons.forEach((button) => {
      button.disabled = !chosen || !this.available;
      // Only the buttons this field is actually holding up. A sold out variant keeps saying
      // sold out instead of borrowing the look of one that is merely waiting for an answer.
      button.classList.toggle('button--awaiting-force', waiting);
    });
    if (this.hint) this.hint.hidden = !waiting;
    this.classList.toggle('force-select--pending', !chosen);
  }

  async checkout(event) {
    event.preventDefault();
    if (this.select.value === '') return;

    const variant = this.form.querySelector('[name="id"]');
    if (!variant || !variant.value) return;

    const quantity = this.form.querySelector('[name="quantity"]');
    const label = this.buyNow.innerHTML;
    this.buyNow.disabled = true;

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variant.value,
              quantity: Number(quantity && quantity.value ? quantity.value : 1),
              properties: { [this.dataset.propertyName]: this.select.value },
            },
          ],
        }),
      });
      if (!response.ok) throw new Error(response.statusText);
      window.location.href = this.dataset.checkoutUrl;
    } catch (error) {
      this.buyNow.disabled = false;
      this.buyNow.innerHTML = label;
      console.error('Could not start the checkout', error);
    }
  }
}

customElements.define('force-select', ForceSelect);
